import * as THREE from 'three';
import { FINALE, PARTICLES, SHOW } from '../config';
import type { Room } from '../room/Room';
import type { DustField } from '../world/DustField';
import type { PaintingLibrary } from '../paintings/PaintingLibrary';

/** ShowDirector가 BGM에 요구하는 최소 인터페이스 (decoupling). */
export interface AudioController {
  setGain(value: number): void;
  restart(): void;
}

type SegmentType =
  | 'calm' // 황금 추상
  | 'bloom' // 명화 피어남 0→1
  | 'life' // 명화 머묾 1
  | 'release' // 명화 용해 1→0
  | 'finaleBloom' // 6면 동시 피어남
  | 'finaleHold' // 6면 + 다채 입자 머묾
  | 'fadeOut' // 서서히 암전
  | 'darkRest'; // 정전 휴식

interface Segment {
  type: SegmentType;
  duration: number;
  painting: number; // 챕터 명화 인덱스 (해당 없으면 -1)
  start: number;
  end: number;
}

function clamp01(x: number): number {
  return Math.min(1, Math.max(0, x));
}
function smoothstep01(x: number): number {
  const t = clamp01(x);
  return t * t * (3 - 2 * t);
}

function buildSegments(paintingCount: number): Segment[] {
  const raw: Array<Pick<Segment, 'type' | 'duration' | 'painting'>> = [];
  raw.push({ type: 'calm', duration: SHOW.introCalm, painting: -1 });
  for (let i = 0; i < paintingCount; i++) {
    raw.push({ type: 'bloom', duration: SHOW.chapter.bloom, painting: i });
    raw.push({ type: 'life', duration: SHOW.chapter.life, painting: i });
    raw.push({ type: 'release', duration: SHOW.chapter.release, painting: i });
    raw.push({ type: 'calm', duration: SHOW.betweenChapters, painting: -1 });
  }
  raw.push({ type: 'finaleBloom', duration: SHOW.finale.bloom, painting: -1 });
  raw.push({ type: 'finaleHold', duration: SHOW.finale.hold, painting: -1 });
  raw.push({ type: 'fadeOut', duration: SHOW.fadeOut, painting: -1 });
  raw.push({ type: 'darkRest', duration: SHOW.darkRest, painting: -1 });

  let t = 0;
  return raw.map((s) => {
    const seg: Segment = { ...s, start: t, end: t + s.duration };
    t += s.duration;
    return seg;
  });
}

/**
 * 쇼 연출가. 경과 시간을 인트로→6챕터→피날레→암전→정전의 세그먼트 시퀀스로
 * 매핑해 매 프레임 방(면별 명화·morph·페이드)·입자(색·다채·페이드)·BGM(게인)을 구동한다.
 * 마지막 정전 뒤 처음부터 무한 반복.
 */
export class ShowDirector {
  private readonly segments: Segment[];
  private readonly totalLength: number;
  private elapsed = 0;

  private readonly goldColor = new THREE.Color(PARTICLES.color);
  private readonly tmpColor = new THREE.Color();
  private readonly finalePalette = FINALE.particlePalette.map((c) => new THREE.Color(c));
  private multicolorOn = false;

  constructor(
    private readonly room: Room,
    private readonly dust: DustField,
    private readonly library: PaintingLibrary,
    private readonly audio?: AudioController,
  ) {
    this.segments = buildSegments(library.count);
    this.totalLength = this.segments.reduce((sum, s) => sum + s.duration, 0);
  }

  get duration(): number {
    return this.totalLength;
  }

  /** 디버그/검증용: 쇼 시계를 특정 시각(초)으로 점프. */
  seek(seconds: number): void {
    this.elapsed = ((seconds % this.totalLength) + this.totalLength) % this.totalLength;
  }

  update(dt: number): void {
    const prev = this.elapsed;
    this.elapsed = (this.elapsed + dt) % this.totalLength;
    if (this.audio && this.elapsed < prev) this.audio.restart(); // 루프 재시작 → BGM 동기

    const seg = this.activeSegment();
    const p = clamp01((this.elapsed - seg.start) / seg.duration);
    this.apply(seg, p);

    if (this.audio) this.audio.setGain(this.audioGainAt(this.elapsed));
  }

  private activeSegment(): Segment {
    for (const seg of this.segments) {
      if (this.elapsed < seg.end) return seg;
    }
    return this.segments[this.segments.length - 1];
  }

  /** BGM 게인 엔벨로프: 인트로 페이드인 → 본편 1 → 끝(암전) 페이드아웃 → 정전 무음. */
  private audioGainAt(t: number): number {
    const fadeOutStart = this.totalLength - SHOW.fadeOut - SHOW.darkRest;
    const darkStart = this.totalLength - SHOW.darkRest;
    if (t < SHOW.introCalm) return smoothstep01(t / SHOW.introCalm);
    if (t < fadeOutStart) return 1;
    if (t < darkStart) return 1 - smoothstep01((t - fadeOutStart) / SHOW.fadeOut);
    return 0;
  }

  private apply(seg: Segment, p: number): void {
    switch (seg.type) {
      case 'calm':
        this.ensureSingleColor();
        this.room.setMorphAll(0);
        this.room.setFadeAll(1);
        this.dust.setFade(1);
        this.dust.setColor(this.goldColor);
        break;
      case 'bloom':
        this.chapterFrame(seg.painting, smoothstep01(p));
        break;
      case 'life':
        this.chapterFrame(seg.painting, 1);
        break;
      case 'release':
        this.chapterFrame(seg.painting, 1 - smoothstep01(p));
        break;
      case 'finaleBloom':
        this.finaleFrame(smoothstep01(p), 1);
        break;
      case 'finaleHold':
        this.finaleFrame(1, 1);
        break;
      case 'fadeOut':
        this.finaleFrame(1, 1 - smoothstep01(p));
        break;
      case 'darkRest':
        this.ensureSingleColor();
        this.room.setMorphAll(0);
        this.room.setFadeAll(0);
        this.dust.setFade(0);
        this.dust.setColor(this.goldColor);
        break;
    }
  }

  /** 한 챕터: 전 면에 같은 명화, 입자색은 황금→그 명화의 별빛으로 morph 보간. */
  private chapterFrame(paintingIndex: number, morph: number): void {
    this.ensureSingleColor();
    const painting = this.library.get(paintingIndex);
    this.room.setPaintingAll(painting.texture, painting.loaded);
    this.room.setMorphAll(morph);
    this.room.setFadeAll(1);
    this.dust.setFade(1);
    this.tmpColor.copy(this.goldColor).lerp(painting.particleColor, morph);
    this.dust.setColor(this.tmpColor);
  }

  /** 피날레: 면마다 다른 명화, 입자는 다채색, fade로 암전. */
  private finaleFrame(morph: number, fade: number): void {
    for (let i = 0; i < this.room.wallCount; i++) {
      const painting = this.library.get(i % this.library.count);
      this.room.setPaintingAt(i, painting.texture, painting.loaded);
    }
    this.ensureMulticolor();
    this.room.setMorphAll(morph);
    this.room.setFadeAll(fade);
    this.dust.setFade(fade);
  }

  private ensureSingleColor(): void {
    if (this.multicolorOn) {
      this.dust.disableMulticolor();
      this.multicolorOn = false;
    }
  }

  private ensureMulticolor(): void {
    if (!this.multicolorOn) {
      this.dust.enableMulticolor(this.finalePalette);
      this.multicolorOn = true;
    }
  }
}
